import { Table } from '@tanstack/react-table';

import { CustomRender, PaginationType } from '../TanstackTable.types';
import { TanstackTableView } from './TanstackTableView';
import { TanstackTableVirtualView } from './TanstackTableVirtualView';

export interface GetTableViewBodyParams<T extends Record<string, any>> {
  table: Table<T>;
  paginationType: PaginationType;
  customRender?: CustomRender<T> | null;
  hideTableFragmentIfEmpty: boolean;
  isDataEmpty: boolean;
  isFilterDataEmpty: boolean;
  loading: boolean;
  emptyStateElement?: React.ReactNode;
  rowHeight?: number;
  loadMoreSkeletonRows?: number;
  nextCursor?: string | null;
  onLoadMore?: (cursor: string) => void;
  loadingMore?: boolean;
  skeletonRows: number;
  layout: 'fixed' | 'auto';
  /** Virtual scroll overflow viewport test id (virtual_scroll + CustomRender). */
  scrollViewportTestId?: string;
  /** Optional `data-testid` on filter-empty FlyEmptyState (virtual_scroll body only). */
  filterEmptyStateTestId?: string;
  /** Pass-through for Table.Root (TableView and VirtualView) */
  rest?: Record<string, unknown>;
  scrollFade?: boolean;
  scrollFadeGradientColor?: string;
  scrollFadeGradientHeight?: number;
}

/**
 * View strategy: returns the table body node (custom render, empty state, virtual view, or default table view).
 */
export function getTableViewBody<T extends Record<string, any>>({
  table,
  paginationType,
  customRender: CustomRenderComponent,
  hideTableFragmentIfEmpty,
  isDataEmpty,
  isFilterDataEmpty,
  loading,
  emptyStateElement,
  rowHeight,
  loadMoreSkeletonRows,
  nextCursor,
  onLoadMore,
  loadingMore,
  skeletonRows,
  layout,
  scrollViewportTestId,
  filterEmptyStateTestId,
  scrollFade = true,
  scrollFadeGradientColor,
  scrollFadeGradientHeight,
  rest = {},
}: GetTableViewBodyParams<T>): React.ReactNode {
  if (CustomRenderComponent) {
    return (
      <CustomRenderComponent
        table={table}
        isDataEmpty={isDataEmpty}
        isFilterDataEmpty={isFilterDataEmpty}
        loading={loading}
        loadingMore={loadingMore}
        nextCursor={nextCursor}
        onLoadMore={onLoadMore}
        scrollViewportTestId={scrollViewportTestId}
        scrollFade={scrollFade}
        scrollFadeGradientColor={scrollFadeGradientColor}
        scrollFadeGradientHeight={scrollFadeGradientHeight}
      />
    );
  }
  if (hideTableFragmentIfEmpty && isDataEmpty && !loading) {
    return emptyStateElement;
  }
  if (paginationType === 'virtual_scroll' && typeof onLoadMore === 'function') {
    return (
      <TanstackTableVirtualView
        table={table}
        isDataEmpty={isDataEmpty}
        isFilterDataEmpty={isFilterDataEmpty}
        loading={loading}
        loadingMore={loadingMore}
        skeletonRows={skeletonRows}
        emptyStateElement={emptyStateElement}
        layout={layout}
        rowHeight={rowHeight}
        loadMoreSkeletonRows={loadMoreSkeletonRows}
        nextCursor={nextCursor}
        onLoadMore={onLoadMore}
        scrollViewportTestId={scrollViewportTestId}
        filterEmptyStateTestId={filterEmptyStateTestId}
        {...rest}
      />
    );
  }
  return (
    <TanstackTableView
      table={table}
      isDataEmpty={isDataEmpty}
      isFilterDataEmpty={isFilterDataEmpty}
      loading={loading}
      skeletonRows={skeletonRows}
      emptyStateElement={emptyStateElement}
      layout={layout}
      {...rest}
    />
  );
}
